import { Link, useLocation } from "react-router-dom";
import { Cookie, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCookieConsent } from "@/hooks/useCookieConsent";
import { CookiePreferencesModal } from "./CookiePreferencesModal";

type Props = {
  /** Prefixes where the banner must never appear (e.g. broadcast share tabs). */
  hideOnPaths?: string[];
};

export const CookieConsentBanner = ({ hideOnPaths = [] }: Props) => {
  const { pathname } = useLocation();
  const {
    showBanner,
    consent,
    acceptAll,
    rejectNonEssential,
    updateConsent,
    openPreferences,
    closePreferences,
    showPreferences,
  } = useCookieConsent();

  const hidden = hideOnPaths.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );
  if (hidden) return null;
  if (!showBanner && !showPreferences) return null;

  return (
    <>
      {/* Banner - Compact slim design */}
      {showBanner && (
        <div className="fixed bottom-0 left-0 right-0 z-50 p-3 animate-in slide-in-from-bottom duration-500">
          <div className="container mx-auto max-w-5xl">
            <div className="bg-card/95 backdrop-blur-md border border-border rounded-xl shadow-lg">
              <div className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
                {/* Compact content row */}
                <div className="flex-1 flex items-center gap-3">
                  <Cookie className="w-5 h-5 text-primary flex-shrink-0" />
                  <p className="text-sm text-muted-foreground">
                    We use cookies to improve your experience.{" "}
                    <Link to="/privacy" className="text-primary hover:underline">Privacy</Link>
                    {" · "}
                    <Link to="/cookies" className="text-primary hover:underline">Cookies</Link>
                  </p>
                </div>
                
                {/* Compact horizontal buttons */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button variant="ghost" size="sm" onClick={openPreferences}>
                    <Settings className="w-4 h-4 mr-1" />
                    Customize
                  </Button>
                  <Button variant="ghost" size="sm" onClick={rejectNonEssential}>
                    Reject
                  </Button>
                  <Button size="sm" onClick={acceptAll}>
                    Accept All
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preferences Modal */}
      <CookiePreferencesModal
        open={showPreferences}
        onClose={closePreferences}
        consent={consent}
        onSave={updateConsent}
        onAcceptAll={acceptAll}
        onRejectNonEssential={rejectNonEssential}
      />
    </>
  );
};
